import React, { useState } from 'react';
import { Map, CloudSun, AlertTriangle, Sliders, Clock } from 'lucide-react';

type OnboardingModalProps = {
  onDismiss: () => void;
};

const PAGES = [
  {
    icon: <Map size={36} color="#007AFF" />,
    title: 'Build your route fast',
    description: 'Search start and destination — the map builds the drive as soon as you pick a place.',
    detail: 'Add up to 3 via stops, reorder them, set dwell times, and compare alternate routes.',
  },
  {
    icon: <CloudSun size={36} color="#FF9500" />,
    title: 'Weather at every waypoint',
    description: 'Open-Meteo hourly samples roughly every 40 miles along your corridor.',
    detail: 'Map pins show conditions right when you arrive. Switch between Temperature, Sky, Wind, and Rain.',
  },
  {
    icon: <AlertTriangle size={36} color="#FF3B30" />,
    title: 'U.S. alert awareness',
    description: 'Active National Weather Service bulletins along your route.',
    detail: 'Corridor pins and alerts control surface severity badges so you know what lies ahead.',
  },
  {
    icon: <Sliders size={36} color="#5856D6" />,
    title: 'Scrub the road ahead',
    description: 'Slide along the route to preview conditions at each moment of your drive.',
    detail: 'The car dot moves along the road with an interactive elevation and temperature profile.',
  },
  {
    icon: <Clock size={36} color="#34C759" />,
    title: 'Leave when you mean to',
    description: 'Minute-level departure scrubber drives forecasts the entire way.',
    detail: 'Scrub departure across the next 7 days with live map preview before hitting the road.',
  },
];

export const OnboardingModal: React.FC<OnboardingModalProps> = ({ onDismiss }) => {
  const [currentPage, setCurrentPage] = useState(0);

  const handleNext = () => {
    if (currentPage < PAGES.length - 1) {
      setCurrentPage((p) => p + 1);
    } else {
      onDismiss();
    }
  };

  const page = PAGES[currentPage]!;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        className="sheet-surface"
        style={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 32,
          padding: '32px 24px',
          boxSizing: 'border-box',
          textAlign: 'center',
          color: 'var(--label)',
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: 36,
              backgroundColor: 'var(--fill3)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 12,
            }}
          >
            {page.icon}
          </div>
          <div className="t-title2" style={{ marginBottom: 8 }}>
            {page.title}
          </div>
          <div className="t-body" style={{ color: 'var(--label2)', marginBottom: 8 }}>
            {page.description}
          </div>
          <div className="t-foot c3" style={{ lineHeight: 1.4 }}>
            {page.detail}
          </div>
        </div>

        {/* Page Dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, margin: '20px 0' }}>
          {PAGES.map((_, i) => (
            <div
              key={i}
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: i === currentPage ? 'var(--accent)' : 'var(--fill2)',
                transition: 'background-color 0.2s ease',
              }}
            />
          ))}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button className="btn-fill" onClick={handleNext}>
            {currentPage === PAGES.length - 1 ? 'Get Started' : 'Next'}
          </button>
          <button
            onClick={onDismiss}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--label2)',
              fontSize: 15,
              fontWeight: 500,
              padding: 8,
              cursor: 'pointer',
            }}
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
};
